/**
 * batch-59 voertuig-afspraken (FO-435 stap 1-2) - pure parser voor de door een
 * garage aangeleverde onderhoudshistorie (Megens, Wassink).
 *
 * De garages leveren een lijst (Excel/PDF-tabel). De behandelaar plakt of
 * uploadt die als tekst; deze parser zet 'm om in gestructureerde regels. Puur,
 * dus volledig testbaar en identiek in API en UI-preview.
 *
 * Ondersteunde scheidingstekens: TAB (Excel-plak), `;` (NL-CSV) en `,` - de
 * eerste die in de kopregel of in de meeste regels voorkomt wint. Kolommen
 * worden op kopnaam herkend; ontbreekt een kopregel, dan geldt de volgorde
 * kenteken / datum / omschrijving / bedrag / km.
 *
 * Bedragen komen als string terug (NOOIT als float): de aanroeper zet ze om naar
 * Prisma.Decimal. Zo gaat er onderweg geen cent verloren en blijft de
 * dedupe-vergelijking exact.
 */

import { normalizeKenteken } from './voertuig-utils'

export interface HistorieImportRegel {
  /** 0-based volgnummer in de aanlevering (idempotentie-sleutel per batch). */
  positie: number
  /** Kenteken zoals aangeleverd (weergave/audit). */
  kentekenRuw: string
  /** Genormaliseerd kenteken; '' = geen leesbaar kenteken. */
  kenteken: string
  /** ISO-dag (yyyy-mm-dd) of null bij een onleesbare datum. */
  datum: string | null
  omschrijving: string | null
  /** Bedrag als decimale string ("1234.56") of null. */
  bedrag: string | null
  kmStand: number | null
}

export interface HistorieImportParseResultaat {
  regels: HistorieImportRegel[]
  /** Herkende kolomvolgorde (voor de UI-preview). */
  kolommen: HistorieKolom[]
  /** Was er een kopregel? */
  kopregel: boolean
  /** Regels die volledig leeg waren of niet te splitsen (informatief). */
  overgeslagen: number
}

export type HistorieKolom = 'kenteken' | 'datum' | 'omschrijving' | 'bedrag' | 'km' | 'onbekend'

const STANDAARD_KOLOMMEN: HistorieKolom[] = ['kenteken', 'datum', 'omschrijving', 'bedrag', 'km']

const KOP_PATRONEN: Array<{ kolom: HistorieKolom; woorden: string[] }> = [
  { kolom: 'kenteken', woorden: ['kenteken', 'licenseplate', 'plate', 'reg'] },
  { kolom: 'datum', woorden: ['datum', 'date', 'werkdatum', 'uitvoerdatum', 'factuurdatum'] },
  {
    kolom: 'omschrijving',
    woorden: ['omschrijving', 'werkzaamheden', 'beschrijving', 'description', 'melding'],
  },
  { kolom: 'bedrag', woorden: ['bedrag', 'totaal', 'amount', 'prijs', 'excl'] },
  { kolom: 'km', woorden: ['km', 'kilometer', 'kmstand', 'tellerstand', 'odometer'] },
]

function bepaalScheidingsteken(regels: string[]): string {
  const kandidaten = ['\t', ';', ',']
  let beste = '\t'
  let besteScore = -1
  for (const teken of kandidaten) {
    const score = regels.reduce((som, r) => som + (r.split(teken).length - 1), 0)
    if (score > besteScore) {
      besteScore = score
      beste = teken
    }
  }
  return besteScore > 0 ? beste : '\t'
}

function herkenKop(cellen: string[]): HistorieKolom[] | null {
  const genormaliseerd = cellen.map((c) => c.trim().toLowerCase().replace(/[^a-z]/g, ''))
  const gevonden = genormaliseerd.map<HistorieKolom>((cel) => {
    if (!cel) return 'onbekend'
    for (const p of KOP_PATRONEN) {
      if (p.woorden.some((w) => cel === w || cel.startsWith(w))) return p.kolom
    }
    return 'onbekend'
  })
  // Een kopregel telt pas als kopregel wanneer minstens kenteken EN datum
  // herkend zijn - anders is het gewoon de eerste datarij.
  if (gevonden.includes('kenteken') && gevonden.includes('datum')) return gevonden
  return null
}

/**
 * Datum uit vrije tekst. Ondersteund: dd-mm-jjjj, dd/mm/jjjj, dd-mm-jj,
 * jjjj-mm-dd. Retourneert een ISO-dag of null. Bewust conservatief: een
 * onleesbare datum wordt ONMATCHBAAR, nooit stil "vandaag".
 */
export function parseHistorieDatum(waarde: string | null | undefined): string | null {
  if (!waarde) return null
  const s = waarde.trim()
  if (!s) return null

  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s)
  if (iso) return bouwIsoDag(Number(iso[1]), Number(iso[2]), Number(iso[3]))

  const nl = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/.exec(s)
  if (nl) {
    const jaarRuw = Number(nl[3])
    const jaar = jaarRuw < 100 ? 2000 + jaarRuw : jaarRuw
    return bouwIsoDag(jaar, Number(nl[2]), Number(nl[1]))
  }
  return null
}

function bouwIsoDag(jaar: number, maand: number, dag: number): string | null {
  if (maand < 1 || maand > 12 || dag < 1 || dag > 31) return null
  if (jaar < 1990 || jaar > 2100) return null
  const d = new Date(Date.UTC(jaar, maand - 1, dag))
  if (d.getUTCFullYear() !== jaar || d.getUTCMonth() !== maand - 1 || d.getUTCDate() !== dag) {
    return null
  }
  return `${String(jaar).padStart(4, '0')}-${String(maand).padStart(2, '0')}-${String(dag).padStart(2, '0')}`
}

/**
 * Bedrag uit vrije tekst naar decimale string. Ondersteunt NL-notatie
 * ("1.234,56"), EN-notatie ("1,234.56") en kale getallen; valuta-tekens en
 * spaties worden genegeerd. Negatieve bedragen (creditregels) blijven negatief.
 */
export function parseHistorieBedrag(waarde: string | null | undefined): string | null {
  if (waarde == null) return null
  let s = waarde.replace(/[€\s ]/g, '').trim()
  if (!s) return null
  let negatief = false
  if (/^\(.*\)$/.test(s)) {
    negatief = true
    s = s.slice(1, -1)
  }
  if (s.startsWith('-')) {
    negatief = true
    s = s.slice(1)
  }
  const heeftKomma = s.includes(',')
  const heeftPunt = s.includes('.')
  if (heeftKomma && heeftPunt) {
    // De laatste van beide is het decimaalteken.
    if (s.lastIndexOf(',') > s.lastIndexOf('.')) s = s.replace(/\./g, '').replace(',', '.')
    else s = s.replace(/,/g, '')
  } else if (heeftKomma) {
    s = s.replace(',', '.')
  } else if (heeftPunt) {
    // Alleen punten: in NL-aanleveringen is dat het DUIZENDTAL-teken ("1.234"),
    // niet het decimaalteken. Een punt met exact drie cijfers erna (en dus geen
    // centen) hoort weggestreept te worden. Zonder deze tak levert "1.234" drie
    // decimalen op, weigert het gedeelde bedrag-schema de regel en faalt de
    // COMPLETE aanlevering op één rij zonder centen.
    if (/^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, '')
  }
  if (!/^\d+(\.\d+)?$/.test(s)) return null
  // Altijd op twee decimalen: het gedeelde schema (en Prisma.Decimal(10,2))
  // accepteert er niet meer, en centen mogen nooit stil verdwijnen op een
  // afrondingsverschil verderop in de keten.
  const genormaliseerd = (Math.round(Number(s) * 100) / 100).toFixed(2)
  if (!/^\d+\.\d{2}$/.test(genormaliseerd)) return null
  return negatief ? `-${genormaliseerd}` : genormaliseerd
}

function parseKm(waarde: string | null | undefined): number | null {
  if (!waarde) return null
  const cijfers = waarde.replace(/[^\d]/g, '')
  if (!cijfers) return null
  const n = Number(cijfers)
  return Number.isFinite(n) && n > 0 && n < 2_000_000 ? n : null
}

/**
 * Parse een geplakte/ge-uploade historie-lijst tot regels.
 *
 * Elke niet-lege regel levert een `HistorieImportRegel`, ook als kenteken of
 * datum onleesbaar is: die regels moeten juist zichtbaar blijven in de
 * review-lijst (ONMATCHBAAR), niet stil verdwijnen.
 */
export function parseVoertuigHistorieTekst(tekst: string): HistorieImportParseResultaat {
  const ruweRegels = tekst
    .split(/\r?\n/)
    .map((r) => r.trimEnd())
    .filter((r) => r.trim().length > 0)

  if (ruweRegels.length === 0) {
    return { regels: [], kolommen: STANDAARD_KOLOMMEN, kopregel: false, overgeslagen: 0 }
  }

  const scheidingsteken = bepaalScheidingsteken(ruweRegels)
  const gesplitst = ruweRegels.map((r) => r.split(scheidingsteken).map((c) => c.trim()))

  const kop = herkenKop(gesplitst[0] ?? [])
  const kolommen = kop ?? STANDAARD_KOLOMMEN
  const dataRegels = kop ? gesplitst.slice(1) : gesplitst

  const regels: HistorieImportRegel[] = []
  let overgeslagen = 0

  for (const cellen of dataRegels) {
    if (cellen.every((c) => c.length === 0)) {
      overgeslagen += 1
      continue
    }
    const cel = (kolom: HistorieKolom): string | null => {
      const index = kolommen.indexOf(kolom)
      if (index < 0) return null
      const waarde = cellen[index]
      return waarde && waarde.length > 0 ? waarde : null
    }
    const kentekenRuw = cel('kenteken') ?? ''
    regels.push({
      positie: regels.length,
      kentekenRuw,
      kenteken: normalizeKenteken(kentekenRuw),
      datum: parseHistorieDatum(cel('datum')),
      omschrijving: cel('omschrijving'),
      bedrag: parseHistorieBedrag(cel('bedrag')),
      kmStand: parseKm(cel('km')),
    })
  }

  return { regels, kolommen, kopregel: kop !== null, overgeslagen }
}
